import type { Vector3Tuple } from "three";

import type { PropDefinition } from "@/types/engine.types";

/** Monument Valley-inspired color palette */
export const COLORS = {
  sand: "#E8D5A3",
  sandDark: "#D4BC82",
  coral1: "#F28B82",
  coral2: "#FBAB7E",
  coral3: "#F7CE68",
  teal: "#6ECFCF",
  tealDark: "#4AA8A8",
  purple: "#B39DDB",
  purpleDeep: "#8E74C4",
  pink: "#F48FB1",
  blue: "#81D4FA",
  green: "#81C784",
  orange: "#FFAB40",
  cream: "#FFF8E1",
  white: "#FFFEFC",
  screenGlow: "#A8E6CF",
  screenBg: "#2D4A3E",
  sky: "#5B8FA8",
} as const;

export type CoralType = "branch" | "fan" | "blob" | "tube";

export interface CoralEntry {
  type: CoralType;
  position: Vector3Tuple;
  color: keyof typeof COLORS;
  scale: number;
}

export const CORAL_LAYOUT: CoralEntry[] = [
  // Edge corals (large, decorative)
  { type: "branch", position: [-6, 0, 2], color: "coral1", scale: 1.2 },
  { type: "fan", position: [-6, 0, -1], color: "coral2", scale: 1.0 },
  { type: "blob", position: [6, 0, -1.5], color: "pink", scale: 0.9 },
  { type: "tube", position: [6, 0, 3], color: "coral1", scale: 1.0 },
  { type: "branch", position: [-1, 0, -5.5], color: "coral3", scale: 0.8 },
  { type: "fan", position: [1.5, 0, -5], color: "teal", scale: 0.9 },
  { type: "blob", position: [-5, 0, -4], color: "coral2", scale: 0.7 },
  { type: "tube", position: [5, 0, -4], color: "purple", scale: 0.8 },
  // Between workstations (smaller, accent)
  { type: "tube", position: [-1.5, 0, 0], color: "coral1", scale: 0.6 },
  { type: "branch", position: [2, 0, 1], color: "coral3", scale: 0.5 },
  { type: "blob", position: [0, 0, -2], color: "teal", scale: 0.5 },
  { type: "fan", position: [-3, 0, 1.5], color: "pink", scale: 0.5 },
];

/* Labels and click feedback follow the brand voice: dry, plain, no
   emoji decoration. Each object names the real control it opens. */
export const PROP_DEFINITIONS: PropDefinition[] = [
  {
    // The computer is the Studio door (apps the agent built). NOTE: this
    // takes the slot that was labeled "Integrations" — Integrations needs a
    // new home (another prop or the settings surface); not resolved here.
    id: "computer",
    label: "Studio",
    clickMessage: "Opening the studio.",
    position: [0, 0, 3],
  },
  {
    id: "telephone",
    label: "Telephone",
    clickMessage: "Opening telephone.",
    position: [-4.5, 0, 0],
  },
  {
    id: "fileCabinet",
    label: "Files",
    clickMessage: "Opening files.",
    position: [3.5, 0, -3],
  },
  {
    id: "calendar",
    label: "Task Manager",
    clickMessage: "Opening the task manager.",
    position: [-3.5, 0, -3.5],
  },
  {
    id: "mailbox",
    label: "Mailbox",
    clickMessage: "Opening the mailbox.",
    position: [4.5, 0, 1.5],
  },
  {
    id: "notebook",
    label: "Notebook",
    clickMessage: "Opening the notebook.",
    position: [-2.5, 0, 3.5],
  },
];
